import React, { useMemo } from 'react';
import { Box, Flex, FlexProps, Text } from 'rebass';
import { StatusStyle } from '../Status/StatusDot';
import StatusChip from '../Status/StatusChip';
import { KeyValLink, KeyValStatusDot } from './KeyValVariants';
import useNotify from '../../hooks/useNotify';

type AccordionItemProps = Omit<FlexProps, 'value'> & {
  label: string;
  value: any;
  oldValue?: any;
  status?: StatusStyle;
  kind?: 'dot' | 'chip' | 'link' | 'text';
  onClick?: () => void;
  href?: string;
};

const AccordionItem: React.FC<AccordionItemProps> = ({
  label = '',
  value = '-',
  oldValue,
  status = 'default',
  kind = 'text',
  onClick,
  href = '#',
  ...props
}): JSX.Element => {
  const { showSuccess } = useNotify();

  const canCopy =
    (typeof value === 'string' || typeof value === 'number') &&
    kind !== 'dot' &&
    value !== '-' &&
    navigator.clipboard;

  const renderedValue = useMemo(() => {
    return (
      <>
        {value !== null &&
          value !== undefined &&
          oldValue !== null &&
          oldValue !== undefined &&
          (oldValue + '').toLowerCase() != (value + '').toLowerCase() && (
            <Box
              sx={{
                textDecoration: 'line-through',
                color: 'text',
                textAlign: 'right',
              }}
            >
              {oldValue}
            </Box>
          )}
        {value}
      </>
    );
  }, [value, oldValue]);
  return (
    <Flex
      justifyContent="space-between"
      py={2}
      {...(props as any)}
      sx={{ gap: 2, ...props.sx }}
    >
      <Text variant="body2" flexShrink={0} color="fieldBorderHover">
        {label}
      </Text>
      {kind === 'link' && (
        <KeyValLink onClick={onClick} href={href}>
          {renderedValue}
        </KeyValLink>
      )}
      {kind === 'chip' && (
        <StatusChip status={status}>{renderedValue}</StatusChip>
      )}
      {kind !== 'link' && kind !== 'chip' && (
        <KeyValStatusDot
          hasStatusDot={kind === 'dot'}
          status={status ?? 'default'}
          title={canCopy ? 'Copy to clipboard' : undefined}
          sx={{
            cursor: canCopy ? 'pointer' : undefined,
            ...(canCopy
              ? {
                  '&:hover': {
                    color: 'primary',
                  },
                }
              : {}),
          }}
          onClick={
            canCopy
              ? () => {
                  navigator.clipboard.writeText(value.toString());
                  showSuccess(`Copied "${value}" to clipboard`, 3000);
                }
              : undefined
          }
        >
          {renderedValue}
        </KeyValStatusDot>
      )}
    </Flex>
  );
};

export default AccordionItem;
